import DOMPurify from 'dompurify';

const SAFE_URL_PATTERN = /^(?:https?:|mailto:|tel:|\/|#|\.)/i;

/**
 * Sanitizes an HTML fragment before it is injected via `innerHTML`.
 * Allows the small set of formatting tags that Contentstack legal/address
 * rich-text fields may contain.
 */
export function sanitizeHtml(dirty: string | undefined | null): string {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'br', 'p', 'span', 'a', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'aria-label', 'class'],
  });
}

/**
 * Escapes a value used inside a quoted HTML attribute. Returns an empty string
 * for unsafe URL schemes (e.g. `javascript:`).
 */
export function sanitizeAttr(value: string | undefined | null): string {
  if (!value) return '';
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Validates and escapes a URL for use in `href`. Blocks unsafe schemes.
 */
export function sanitizeUrl(value: string | undefined | null): string {
  if (!value) return '';
  const trimmed = value.trim();
  if (!SAFE_URL_PATTERN.test(trimmed)) return '';
  return sanitizeAttr(trimmed);
}

/**
 * Validates a CSS value used in a `style` attribute (e.g. a background color).
 * Rejects anything containing characters that could break out of the property.
 */
export function sanitizeCSS(value: string | undefined | null): string {
  if (!value) return '';
  const trimmed = value.trim();
  if (/[<>{};]|url\(|expression|javascript:/i.test(trimmed)) return '';
  return trimmed;
}
